/**
 * Notificatie Service
 * In-app notificaties via Firestore + optioneel Web Push via FCM
 */
import { db } from '../config/firebase';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDocs,
  query, where, orderBy, limit, onSnapshot, serverTimestamp, writeBatch,
} from 'firebase/firestore';

// ========== NOTIFICATIES CRUD ==========

/**
 * Subscribe to notifications for a user (real-time)
 * Uses composite index (ontvangerId + aangemaakt desc).
 * Falls back to simple query + client-side sort if index is missing.
 */
export function subscribeNotificaties(userId, callback) {
  // Primaire query met orderBy (vereist composite index)
  const q = query(
    collection(db, 'notificaties'),
    where('ontvangerId', '==', userId),
    orderBy('aangemaakt', 'desc'),
    limit(50)
  );

  let useFallback = false;
  const unsub = onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      aangemaakt: d.data().aangemaakt?.toDate?.() || new Date(),
    })));
  }, (error) => {
    // Index missing — fallback naar simpele query zonder orderBy
    if (!useFallback && (error.code === 'failed-precondition' || error.message?.includes('index'))) {
      useFallback = true;
      console.warn('Notificaties: composite index ontbreekt, fallback naar simpele query.\n' +
        'Maak de index aan via Firebase Console → Firestore → Indexes:\n' +
        '  Collection: notificaties | Fields: ontvangerId ASC, aangemaakt DESC\n' +
        'Of klik op de link in de volgende foutmelding:', error.message);

      // Fallback: query zonder orderBy, sort client-side
      const qFallback = query(
        collection(db, 'notificaties'),
        where('ontvangerId', '==', userId),
        limit(100)
      );
      onSnapshot(qFallback, (snap) => {
        const notifs = snap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          aangemaakt: d.data().aangemaakt?.toDate?.() || new Date(),
        }));
        // Sort client-side: nieuwste eerst
        notifs.sort((a, b) => b.aangemaakt - a.aangemaakt);
        callback(notifs.slice(0, 50));
      });
    } else {
      console.error('Notificaties subscription error:', error);
    }
  });

  return unsub;
}

/**
 * Create a notification for one or more recipients
 */
export async function maakNotificatie({ ontvangerIds, type, titel, bericht, link, afzenderId, afzenderNaam }) {
  const batch = writeBatch(db);
  const ids = Array.isArray(ontvangerIds) ? ontvangerIds : [ontvangerIds];

  for (const ontvangerId of ids) {
    if (!ontvangerId) continue;
    const ref = doc(collection(db, 'notificaties'));
    batch.set(ref, {
      ontvangerId,
      type: type || 'info',
      titel: titel || '',
      bericht: bericht || '',
      link: link || '/',
      gelezen: false,
      afzenderId: afzenderId || '',
      afzenderNaam: afzenderNaam || '',
      aangemaakt: serverTimestamp(),
    });
  }

  await batch.commit();

  // Try to send push notifications (non-blocking)
  try {
    await stuurPushNaarGebruikers(ids, titel, bericht, link);
  } catch (e) {
    console.warn('Push notificatie niet verstuurd:', e.message);
  }
}

/**
 * Mark notification as read
 */
export async function markeerGelezen(notificatieId) {
  await updateDoc(doc(db, 'notificaties', notificatieId), { gelezen: true });
}

/**
 * Mark all notifications as read for a user
 */
export async function markeerAllesGelezen(userId) {
  const q = query(
    collection(db, 'notificaties'),
    where('ontvangerId', '==', userId),
    where('gelezen', '==', false)
  );
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.update(d.ref, { gelezen: true }));
  await batch.commit();
}

/**
 * Delete old notifications (> 30 days)
 */
export async function verwijderOudeNotificaties(userId) {
  const dertigDagenGeleden = new Date();
  dertigDagenGeleden.setDate(dertigDagenGeleden.getDate() - 30);

  const q = query(
    collection(db, 'notificaties'),
    where('ontvangerId', '==', userId),
  );
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach(d => {
    const datum = d.data().aangemaakt?.toDate?.();
    if (datum && datum < dertigDagenGeleden) batch.delete(d.ref);
  });
  await batch.commit();
}

// ========== PUSH TOKENS ==========

/**
 * Save push token for a user
 */
export async function slaTokenOp(userId, token) {
  if (!userId || !token) return;
  // Check if token already exists
  const q = query(
    collection(db, 'pushTokens'),
    where('userId', '==', userId),
    where('token', '==', token)
  );
  const snap = await getDocs(q);
  if (!snap.empty) return; // already stored

  await addDoc(collection(db, 'pushTokens'), {
    userId,
    token,
    platform: 'web',
    aangemaakt: serverTimestamp(),
  });
}

/**
 * Remove push token
 */
export async function verwijderToken(userId, token) {
  const q = query(
    collection(db, 'pushTokens'),
    where('userId', '==', userId),
    where('token', '==', token)
  );
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
}

/**
 * Send push notification to users via FCM (calls serverless function)
 */
async function stuurPushNaarGebruikers(userIds, titel, bericht, link) {
  if (!userIds || userIds.length === 0) return;

  // Get all push tokens for these users
  const tokens = [];
  for (const userId of userIds) {
    const q = query(collection(db, 'pushTokens'), where('userId', '==', userId));
    const snap = await getDocs(q);
    snap.docs.forEach(d => {
      if (d.data().token) tokens.push(d.data().token);
    });
  }

  if (tokens.length === 0) return;

  // Call serverless API to send push
  try {
    await fetch('/api/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tokens, titel, bericht, link }),
    });
  } catch (e) {
    console.warn('Push API niet bereikbaar:', e.message);
  }
}

// ========== NOTIFICATION TYPE HELPERS ==========

const NOTIFICATIE_ICONS = {
  verlof_ingediend: '✈️',
  verlof_goedgekeurd: '✅',
  verlof_afgekeurd: '❌',
  verlof_ingetrokken: '↩️',
  ziekmelding: '🏥',
  betermelding: '💪',
  overuren_ingediend: '⏰',
  overuren_goedgekeurd: '✅',
  overuren_afgekeurd: '❌',
  overuren_uitbetaald: '💰',
  flex_ingepland: '⚡',
  flex_gewijzigd: '🔄',
  flex_verwijderd: '🗑️',
  systeem: 'ℹ️',
};

export function getNotificatieIcon(type) {
  return NOTIFICATIE_ICONS[type] || 'ℹ️';
}

// ========== WEB PUSH PERMISSION ==========

/**
 * Register Firebase messaging service worker
 */
export async function registreerPushServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.info('Push SW geregistreerd:', reg.scope);
    return reg;
  } catch (e) {
    console.warn('Push SW registratie mislukt:', e.message);
    return null;
  }
}

/**
 * Request push notification permission and get FCM token
 * vapidKey must be provided (from Firebase Console > Project Settings > Cloud Messaging)
 * Returns token string or null
 */
export async function vraagPushPermissie(vapidKey) {
  if (!('Notification' in window)) {
    console.info('Push: Notification API niet beschikbaar');
    return null;
  }
  if (!('serviceWorker' in navigator)) {
    console.info('Push: Service Worker niet beschikbaar');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.info('Push: permissie geweigerd door gebruiker');
    return null;
  }

  if (!vapidKey) {
    console.info('Push: geen VAPID key — alleen in-app notificaties actief');
    // Show local notification als fallback
    return 'local-only';
  }

  try {
    const reg = await registreerPushServiceWorker();
    if (!reg) return null;

    const { getMessaging, getToken } = await import('firebase/messaging');
    const { default: app } = await import('../config/firebase');
    const messaging = getMessaging(app);

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: reg,
    });
    console.info('Push token verkregen:', token?.substring(0, 20) + '...');
    return token || null;
  } catch (e) {
    console.warn('FCM token ophalen mislukt:', e.message);
    return null;
  }
}

/**
 * Show a local notification (fallback when FCM is not configured)
 */
export function toonLokaleNotificatie(titel, bericht, link) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const notif = new Notification(titel, {
      body: bericht,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      tag: 'sl-verlof-' + Date.now(),
    });
    notif.onclick = () => {
      window.focus();
      if (link) window.location.href = link;
    };
  } catch (e) {
    // Fallback for environments where Notification constructor fails
    navigator.serviceWorker?.ready?.then(reg => {
      reg.showNotification(titel, {
        body: bericht,
        icon: '/icon-192.png',
        tag: 'sl-verlof-' + Date.now(),
      });
    });
  }
}
